// RigSentinel — centralized CLIENT pricing display config + billing helpers.
// Amounts here are DISPLAY ONLY; Paddle price objects are the billing truth.
import { useEffect, useState } from "react";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

export const TRIAL_DAYS = 7;

export const PRICING = {
  pro: {
    monthly: { amount: 20, label: "$20/month" },
    annual: { amount: 200, label: "$200/year", savings: "$40" },
  },
  founding: {
    monthly: { amount: 10, label: "$10/month", normally: "$20" },
    annual: { amount: 100, label: "$100/year", normally: "$200" },
  },
  enterprise: {
    monthly: { amount: 250, label: "$250/month" },
    annual: { amount: 2500, label: "$2,500/year", savings: "$500" },
  },
};

export const TRIAL_COPY =
  "Try RigSentinel Pro free for 7 days. Payment details are required. " +
  "Cancel during the trial to avoid the first subscription charge.";

// New LIVE Pro price IDs (env override → hardcoded live fallback).
export const PRO_PRICE_IDS_V2 = {
  monthly:
    process.env.REACT_APP_PADDLE_PRICE_ID_PRO_MONTHLY_V2 ||
    "pri_01kze02mzxzj166cm871wjb24a",
  annual:
    process.env.REACT_APP_PADDLE_PRICE_ID_PRO_ANNUAL_V2 ||
    "pri_01kze0pkregw8d5shxgy56ydng",
};

export function fetchFounding500Status(fresh = false) {
  return fetch(`${API_BASE}/api/founding500/status${fresh ? "?fresh=1" : ""}`)
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);
}

// "327 FOUNDING PLACES REMAINING" / "1 FOUNDING PLACE REMAINING"
export function formatFoundingRemaining(n) {
  return `${n} FOUNDING PLACE${n === 1 ? "" : "S"} REMAINING`;
}

const MIN_POLL_MS = 30000;

/**
 * Live Founding 500 status — Paddle-backed via GET /api/founding500/status.
 * Polls quietly (default 60s, never faster than 30s), updates state ONLY when
 * the server value actually changes (no fake movement), cleans up its timer
 * on unmount. Never computes/estimates the count client-side.
 */
export function useFounding500(pollMs = 60000) {
  const [state, setState] = useState({ loading: true, error: false, data: null });

  useEffect(() => {
    let alive = true;
    const load = async () => {
      const data = await fetchFounding500Status(false);
      if (!alive) return;
      setState((prev) => {
        if (data == null) return { loading: false, error: true, data: prev.data };
        const p = prev.data;
        const changed = !p || p.remaining !== data.remaining ||
          p.claimed !== data.claimed || p.offer_active !== data.offer_active;
        return changed ? { loading: false, error: false, data }
                       : { ...prev, loading: false, error: false };
      });
    };
    load();
    const id = setInterval(load, Math.max(MIN_POLL_MS, pollMs));
    return () => { alive = false; clearInterval(id); };
  }, [pollMs]);

  return state;
}

// Fire founding500_counter_viewed at most once per source per session
// (module-level dedup — NOT re-sent on every poll).
const _counterViewed = new Set();

export function trackFoundingCounterViewed(source, data) {
  if (!data || _counterViewed.has(source)) return;
  _counterViewed.add(source);
  trackBillingEvent("founding500_counter_viewed", {
    source, remaining: data.remaining, claimed: data.claimed, founding: true,
  });
}

// ---- Lightweight funnel analytics (fire-and-forget, no card data) ----------
const SESSION_KEY = "rsc.analyticsSession.v1";

function sessionId() {
  try {
    let id = localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

export function trackBillingEvent(event, extra = {}) {
  try {
    fetch(`${API_BASE}/api/analytics/track`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, sessionId: sessionId(), ...extra }),
      keepalive: true,
    }).catch(() => {});
  } catch { /* never block UI */ }
}
